import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { 
  Sparkles, ArrowLeft, Copy, Trash2, Download, FileText,
  PenTool, Video, Zap, Camera, Search, Filter, Clapperboard, Lightbulb
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Logo from '@/components/Logo';
import AppNav from '@/components/AppNav';
import SEO from '@/components/SEO';
import AppShell from '@/components/AppShell';

const typeIcons = {
  'text-hook': <PenTool className="w-4 h-4" />,
  'visual-hook': <Video className="w-4 h-4" />,
  'verbal-hook': <Zap className="w-4 h-4" />,
  'filming-idea': <Clapperboard className="w-4 h-4" />,
  'filming-angle': <Camera className="w-4 h-4" />,
  'skit-idea': <Lightbulb className="w-4 h-4" />,
  'script': <FileText className="w-4 h-4" />,
};

const typeLabels = {
  'text-hook': 'Text Hook',
  'visual-hook': 'Visual Hook',
  'verbal-hook': 'Verbal Hook',
  'filming-idea': 'Filming Idea',
  'filming-angle': 'Filming Angle',
  'skit-idea': 'Skit Idea',
  'script': 'Script',
};

const SavedIdeasPage = () => {
  const [savedItems, setSavedItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    const items = JSON.parse(localStorage.getItem('kylo_saved_ideas') || '[]');
    setSavedItems(items);
  }, []);

  const deleteItem = (itemId) => {
    const updatedItems = savedItems.filter(item => item.id !== itemId);
    setSavedItems(updatedItems);
    localStorage.setItem('kylo_saved_ideas', JSON.stringify(updatedItems));
    toast.success('Item removed from saved ideas');
  };

  const clearAll = () => {
    setSavedItems([]);
    localStorage.removeItem('kylo_saved_ideas');
    toast.success('All saved ideas cleared');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const exportAsText = () => {
    if (savedItems.length === 0) {
      toast.error('No items to export');
      return;
    }

    let content = '=== POETTIK - TikTok Shop Wizard ===\n';
    content += 'Saved Content Ideas\n';
    content += '================================\n\n';

    const groupedItems = savedItems.reduce((acc, item) => {
      const type = typeLabels[item.type] || item.type;
      if (!acc[type]) acc[type] = [];
      acc[type].push(item);
      return acc;
    }, {});

    Object.entries(groupedItems).forEach(([type, items]) => {
      content += `\n--- ${type} ---\n\n`;
      items.forEach((item, index) => {
        content += `${index + 1}. ${item.content}\n`;
        content += `   Product: ${item.productName}\n\n`;
      });
    });

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'kylo-saved-ideas.txt';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Ideas exported successfully!');
  };

  const filteredItems = savedItems.filter(item => {
    const matchesSearch = item.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.productName?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterType === 'all' || item.type === filterType;
    return matchesSearch && matchesFilter;
  });

  return (
    <AppShell title="Saved ideas">
      <SEO title="Saved Ideas" description="Your bookmarked Poettik ideas. Build your personal content library." noindex />

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-10">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="font-display text-3xl sm:text-4xl font-bold mb-2">
                Saved <span className="gradient-text">Ideas</span>
              </h1>
              <p className="text-muted-foreground">
                {savedItems.length} {savedItems.length === 1 ? 'idea' : 'ideas'} saved
              </p>
            </div>
            {savedItems.length > 0 && (
              <div className="flex gap-3">
                <Button variant="outline" size="sm" onClick={exportAsText}>
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
                <Button variant="outline" size="sm" onClick={clearAll} className="text-destructive hover:text-destructive">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        {savedItems.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search saved ideas..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="text-hook">Text Hooks</SelectItem>
                <SelectItem value="visual-hook">Visual Hooks</SelectItem>
                <SelectItem value="verbal-hook">Verbal Hooks</SelectItem>
                <SelectItem value="filming-idea">Filming Ideas</SelectItem>
                <SelectItem value="skit-idea">Skit Ideas</SelectItem>
                <SelectItem value="script">Scripts</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Empty State */}
        {savedItems.length === 0 && (
          <Card variant="glass" className="p-12 text-center animate-fade-in-up">
            <div className="w-16 h-16 rounded-2xl bg-muted mx-auto mb-6 flex items-center justify-center">
              <FileText className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-display text-xl font-semibold mb-3">No Saved Ideas Yet</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Start generating content ideas and save your favorites for later use
            </p>
            <Link to="/generator">
              <Button variant="gradient">
                <Sparkles className="w-4 h-4 mr-2" />
                Generate Content
              </Button>
            </Link>
          </Card>
        )}

        {/* Saved Items Grid */}
        {filteredItems.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item) => (
              <Card key={item.id} variant="hover" className="flex flex-col group">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center text-primary">
                        {typeIcons[item.type]}
                      </div>
                      <span className="text-sm font-medium text-muted-foreground">
                        {typeLabels[item.type]}
                      </span>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => copyToClipboard(item.content)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => deleteItem(item.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1">
                  <p className={`text-sm text-foreground leading-relaxed ${item.type === 'script' ? 'whitespace-pre-wrap' : ''}`}>
                    {item.content.length > 200 ? `${item.content.substring(0, 200)}...` : item.content}
                  </p>
                  {item.productName && (
                    <div className="mt-4 pt-4 border-t border-border/50">
                      <p className="text-xs text-muted-foreground">
                        Product: <span className="text-foreground">{item.productName}</span>
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* No results */}
        {savedItems.length > 0 && filteredItems.length === 0 && (
          <Card variant="glass" className="p-8 text-center">
            <p className="text-muted-foreground">No ideas match your search criteria</p>
          </Card>
        )}
      </main>
    </AppShell>
  );
};

export default SavedIdeasPage;
